import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api";
import { navigate, usePathname } from "../../lib/router";
import type { Client } from "../../types";
import { ClientInfoCard } from "./components/ClientInfoCard";
import { PaymentsSection } from "./components/PaymentsSection";
import { RecurringPlansSection } from "./components/RecurringPlansSection";
import { HistorySection } from "./components/HistorySection";
import { UpcomingAppointmentsSection } from "./components/UpcomingAppointmentsSection";
import { ClientMetrics } from "./components/ClientMetrics";
import { DeleteClientButton } from "./components/DeleteClientButton";
import { ContractsSection } from "./components/ContractsSection";
import { EvolutionModal } from "../appointments/components/EvolutionModal";
import { ChevronLeft } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { clientDetailsLabels } from "./labels";

export default function ClientDetailPage() {
  const { lang } = useLanguage();
  const path = usePathname();
  const clientId = useMemo(() => {
    const match = path.match(/\/app\/clients\/([^/]+)/);
    const val = match ? match[1] : null;
    if (val === "undefined" || val === "null") return null;
    return val;
  }, [path]);

  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
   const [history, setHistory] = useState<any[]>([]);
   const [timelineEvents, setTimelineEvents] = useState<import("../../types").TimelineEvent[]>([]);
   const [metricsHistory, setMetricsHistory] = useState<any[]>([]);
   const [payments, setPayments] = useState<import("../../types").Payment[]>([]);
   const [plans, setPlans] = useState<import("../../types").RecurringPlan[]>([]);
   const [contracts, setContracts] = useState<any[]>([]);
   const [hasMoreHist, setHasMoreHist] = useState(true);
   const [loadingHist, setLoadingHist] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [contractRefreshKey, setContractRefreshKey] = useState(0);
  const [evolutionApptId, setEvolutionApptId] = useState<number | null>(null);
  const histLoader = useRef<HTMLDivElement | null>(null);

  const loadHistory = async () => {
    if (!clientId || loadingHist || !hasMoreHist) return;
    setLoadingHist(true);
    const res = await api.listClientAppointments(clientId, { offset: history.length, limit: 10 });
    if (res.ok) {
      setHistory(prev => {
        const ids = new Set(prev.map(h => h.id));
        const merged = [...prev];
        for (const item of res.data) {
          if (!ids.has(item.id)) {
            ids.add(item.id);
            merged.push(item);
          }
        }
        return merged;
      });
      if (res.data.length < 10) setHasMoreHist(false);
    }
    setLoadingHist(false);
  };

   const loadMetricsData = async () => {
     if (!clientId) return;
     setLoadingMetrics(true);
     const res = await api.listClientAppointments(clientId, { limit: 1000 });
     if (res.ok) setMetricsHistory(res.data || []);
     setLoadingMetrics(false);
   };

   const loadTimeline = async () => {
     if (!clientId) return;
     const res = await api.listClientTimeline(clientId, { limit: 100 });
     if (res.ok) setTimelineEvents(Array.isArray(res.data) ? res.data : []);
   };

  useEffect(() => {
    setHistory([]);
    setHasMoreHist(true);
    setMetricsHistory([]);
  }, [clientId]);

   useEffect(() => { 
     loadHistory();
     loadMetricsData();
     loadTimeline();
   }, [clientId]);

  useEffect(() => {
    const el = histLoader.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadHistory();
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [histLoader, history, hasMoreHist, loadingHist]);

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    const [c, pays, rplans, ctrs] = await Promise.all([
      api.getClient(clientId),
      api.listClientPayments(clientId),
      api.listClientRecurringPlans(clientId, { include_inactive: true }),
      api.listClientContracts(clientId),
    ] as const);
    if (c.ok) setClient(c.data);
    if (pays.ok) setPayments(pays.data || []);
    if (rplans.ok) setPlans(rplans.data || []);
    if (ctrs.ok) setContracts(ctrs.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [clientId, contractRefreshKey]);
  if (!clientId) return <div className="p-4">Cliente inválido</div>;

  return (
    <>
      <div className="min-h-screen bg-[#f8fafc]">
        <div className="mx-auto max-w-[1400px] p-4 md:p-8">
            {/* Page Header */}
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/app/clients")}
                  className="p-2 hover:bg-white rounded-2xl transition-all text-neutral-400 hover:text-primary border border-transparent hover:border-neutral-100 shadow-sm hover:shadow-none bg-neutral-50/50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-neutral-900 tracking-tight">
                  {client?.name || clientDetailsLabels[lang].detailPage.header}
                </h1>
                </div>
              </div>

              <ClientInfoCard
                client={client}
                loading={loading}
                onEdit={() => navigate(`/app/clients/${clientId}/edit`)}
              />
            </div>

            {/* Main Content */}
            <main className="flex flex-col gap-8 mt-8">
              
              {/* Top Stats Row */}
              <ClientMetrics appointments={metricsHistory} loading={loadingMetrics} />

              {/* Upcoming & Plans Group */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                <div className="flex flex-col gap-8">
                  <UpcomingAppointmentsSection appointments={history} />
                  <RecurringPlansSection 
                    plans={plans} 
                    client={client} 
                    contracts={contracts}
                    onContractGenerated={() => setContractRefreshKey(k => k + 1)} 
                  />
                </div>

                {/* History / Timeline */}
                <div className="xl:row-span-2">
                  <HistorySection
                    history={history}
                    timelineEvents={timelineEvents}
                    clientId={clientId}
                    loading={loadingHist}
                    hasMore={hasMoreHist}
                    loaderRef={histLoader}
                    onNeedEvolution={(h) => setEvolutionApptId(h.id)}
                    onTimelineRefresh={(newEvent) => {
                      if (newEvent) {
                        setTimelineEvents(prev => {
                          const current = Array.isArray(prev) ? prev : [];
                          const exists = current.some(e => e.id === newEvent.id);
                          if (exists) return current;
                          return [newEvent, ...current];
                        });
                      } else {
                        loadTimeline();
                      }
                    }}
                  />
                </div>
              </div>

              {/* Financial Sections */}
              <div className="grid grid-cols-1 gap-8">
                <PaymentsSection
                  payments={payments}
                  onUpdated={(p) => setPayments(prev => {
                    if ((p as any).total_amount_cents === -1) return prev.filter(x => x.id !== p.id);
                    const exists = prev.some(x => x.id === p.id);
                    return exists ? prev.map(x => x.id === p.id ? p : x) : [...prev, p];
                  })}
                  onRefreshed={(list) => setPayments(list)}
                />
                <ContractsSection key={contractRefreshKey} clientId={clientId} clientName={client?.name || ""} />
              </div>

              <div className="mt-4 flex justify-end">
                <DeleteClientButton client={client} onDeleted={() => navigate("/app/clients")} />
              </div>

            </main>
        </div>
      </div>

      <EvolutionModal
        appointmentId={evolutionApptId}
        isOpen={!!evolutionApptId}
        onClose={() => setEvolutionApptId(null)}
        onSuccess={() => {
          setEvolutionApptId(null);
          loadHistory(); // Refresh history to update evolution status
        }}
      />
    </>
  );
}
